import { LabelingTypeEnum } from 'src/types/tags';
import { DisplayConfig } from 'src/types/readings/readings';

// Above this many distinct values a tag is treated as continuous: labels are meant for
// discrete tags (mode/state/alarm), and forcing a tick per value on an analog series
// would flood the axis.
const MAX_LABELLED_VALUES = 20;

// Renders a y-axis value using the tag's labelling config. Mirrors formatValue in
// src/features/widgets/config/config.ts, which does the same job for the TrendWidget.
//
// dataValues is the set of values that can be named — the tag's configured options and the
// whole numbers actually read. Chart.js generates ticks at even intervals, so it will ask us
// to format positions that are neither (2.5 between 2 and 3). Labelling those would invent
// states that don't exist, so they render blank — the same onlyRealValues guard the widget
// applies. Pass nothing to label every value, which is what the tooltip wants: a hovered
// point is a real reading by definition.
export const formatValueLabel = (
  value: number,
  display?: DisplayConfig | null,
  dataValues?: number[]
): string => {
  if (!display) return String(value);

  const { mode, labels, defaultLabel } = display;

  if (mode === LabelingTypeEnum.VALUE) return String(value);

  const isRealDataValue = dataValues?.length ? dataValues.includes(value) : true;
  if (!isRealDataValue) return '';

  const mappedLabel = labels?.[String(value)];

  switch (mode) {
    case LabelingTypeEnum.LABEL:
      return mappedLabel ?? defaultLabel;

    case LabelingTypeEnum.VALUE_LABEL:
      return `${value} - ${mappedLabel ?? defaultLabel}`;

    default:
      return String(value);
  }
};

// Every value the tag has a label configured for. Mirrors predefinedOptionValues in the
// TrendWidget: a discrete tag shows all of its options on the axis, not only the ones a
// reading happened to land on inside the selected time frame.
export const getPredefinedOptionValues = (display?: DisplayConfig | null): number[] => {
  if (!display?.labels) return [];

  return Object.keys(display.labels)
    .map(Number)
    .filter((value) => !Number.isNaN(value));
};

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

// Pins the axis so every tick lands on a value that can be named — otherwise each tick
// formats to '' and the axis renders empty.
//
// The grid comes from labelledValues (the tag's configured options plus the whole numbers
// actually read), never from the readings alone: trends aggregates, so a mode tag arrives
// as five-minute averages (0.37 somewhere between Off and On) and no tick would ever
// coincide with a reading. The range still has to cover the real data, so it is widened
// outward along that same grid rather than to the raw data min/max, which would knock every
// tick off the labelled values.
const buildDiscreteBounds = (
  labelledValues: number[],
  dataValues: number[]
): Record<string, any> => {
  const sorted = [...new Set(labelledValues)].sort((a, b) => a - b);

  if (sorted.length < 2) return {};
  if (!sorted.every((value) => Number.isInteger(value))) return {};

  const step = sorted
    .slice(1)
    .map((value, index) => value - sorted[index])
    .reduce(gcd);
  if (!step) return {};

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const onGrid = (value: number, round: (n: number) => number) =>
    first + round((value - first) / step) * step;

  const min = onGrid(
    dataValues.reduce((lowest, value) => (value < lowest ? value : lowest), first),
    Math.floor
  );
  const max = onGrid(
    dataValues.reduce((highest, value) => (value > highest ? value : highest), last),
    Math.ceil
  );

  // Guards the same thing MAX_LABELLED_VALUES always guarded — a tick per value only works
  // for a handful of them — but counts the ticks this range actually produces, since an
  // outlier reading can stretch it well past the configured options.
  if ((max - min) / step + 1 > MAX_LABELLED_VALUES) return {};

  return { min, max, stepSize: step };
};

const buildYAxis = (
  tag: any,
  display?: DisplayConfig | null,
  dataValues?: number[],
  isOnlyAxis = false
): Record<string, any> => {
  const isLabelled =
    !!display &&
    (display.mode === LabelingTypeEnum.LABEL || display.mode === LabelingTypeEnum.VALUE_LABEL);

  const readings = dataValues ?? [];

  // The values that can be given a name: every option configured on the tag, plus the whole
  // numbers actually read (a value with no option of its own still falls back to the tag's
  // undefined label). Averaged readings are deliberately left out — they sit between states
  // and naming them would invent one.
  const labelledValues = isLabelled
    ? [
        ...new Set([
          ...getPredefinedOptionValues(display),
          ...readings.filter((value) => Number.isInteger(value)),
        ]),
      ]
    : [];

  // A labelled tag with nothing to label it by falls back to the readings, so it keeps
  // behaving as it did rather than stamping the undefined label onto every tick.
  const tickValues = labelledValues.length ? labelledValues : readings;

  const axis: Record<string, any> = {
    type: 'linear',
    display: true,
    position: 'left',
    ticks: {
      color: tag?.color,
      callback: (value: number | string) => formatValueLabel(Number(value), display, tickValues),
    },
    grid: {
      drawOnChartArea: false,
    },
  };

  if (!tag?.yAxisAuto && tag?.yAxisFrom != null && tag?.yAxisTo != null) {
    axis.min = tag.yAxisFrom;
    axis.max = tag.yAxisTo;
    return axis;
  }

  // An explicit range set on the tag always wins, so this only runs on auto-scaled axes.
  if (isLabelled && labelledValues.length) {
    const { min, max, stepSize } = buildDiscreteBounds(labelledValues, readings);
    if (stepSize != null) {
      axis.min = min;
      axis.max = max;
      axis.ticks.stepSize = stepSize;

      // The widget puts a light rule at each discrete value (annotations.yaxis in
      // src/features/widgets/config/config.ts). One tick per value means Chart.js gridlines
      // land in the same places, but only one axis can draw them without several sets of
      // rules overlapping — so this is limited to charts with a single tag.
      if (isOnlyAxis) {
        axis.grid = { drawOnChartArea: true, color: '#e0e0e0' };
      }
    }
  }

  return axis;
};

export const generateYAxesWithColors = (
  tags: any[],
  displayByTagIndex: Record<number, DisplayConfig | null> = {},
  dataValuesByTagIndex: Record<number, number[]> = {}
) => {
  if (tags.length === 0) return {};

  const yAxes: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/dot-notation
  yAxes['y'] = buildYAxis(
    tags[0],
    displayByTagIndex[0],
    dataValuesByTagIndex[0],
    tags.length === 1
  );

  tags.slice(1).forEach((tag, index) => {
    // tags[1] renders on axis 'y0', tags[2] on 'y1', ... so the tag index is index + 1.
    const tagIndex = index + 1;
    yAxes[`y${index}`] = buildYAxis(
      tag,
      displayByTagIndex[tagIndex],
      dataValuesByTagIndex[tagIndex]
    );
  });

  return yAxes;
};
